<template>
  <el-dialog 
    title="请选择执业药师" 
    :visible.sync="dialogVisible" 
    width="400px" 
    append-to-body
    @close="handleClose" 
    @opened="handleOpened"
    @keydown.native="handleDialogKeyDown"
    :close-on-press-escape="true" 
    :modal-append-to-body="false"
  >
    <!-- 处方药信息展示 -->
    <div 
      v-if="pendingProduct"
      style="margin-bottom: 15px; padding: 10px; background: #f0f9ff; border-radius: 4px; border-left: 4px solid #409EFF;"
    >
      <div style="color: #409EFF; font-weight: bold; margin-bottom: 5px;">处方药信息：</div>
      <div style="color: #666; font-size: 14px;">
        {{ pendingProduct.bgmName }} - {{ pendingProduct.spec }}
      </div>
    </div>

    <!-- 执业药师选择表单 -->
    <el-form 
      id="pharmacistSelectForm" 
      :model="form" 
      :rules="rules" 
      ref="pharmacistSelectFormRef"
      label-width="80px" 
      size="small" 
      @keydown="handleFormKeyDown"
    >
      <el-form-item label="执业药师" prop="pharmacist">
        <el-select 
          ref="pharmacistSelect" 
          v-model="form.pharmacist" 
          placeholder="请选择执业药师"
          style="width: 100%" 
          @keydown.native="handleSelectKeyDown"
          @visible-change="onSelectVisibleChange"
        >
          <el-option 
            v-for="pharmacist in pharmacistList" 
            :key="pharmacist.opmId" 
            :label="pharmacist.opmName"
            :value="pharmacist.opmId" 
          />
        </el-select>
      </el-form-item>
    </el-form>

    <!-- 底部按钮 -->
    <span slot="footer" class="dialog-footer">
      <el-button 
        ref="pharmacistConfirmButton" 
        type="primary" 
        @click="handleConfirm"
        @keydown.enter.native="handleConfirm"
      >
        确认
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: 'PharmacistSelectDialog',
  props: {
    // 弹窗显示状态
    visible: {
      type: Boolean,
      default: false
    },
    // 待添加的处方药商品信息
    pendingProduct: {
      type: Object,
      default: () => null
    },
    // 执业药师列表
    pharmacistList: {
      type: Array,
      default: () => []
    },
    // 当前选中的执业药师ID
    selectedPharmacist: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      // 表单数据
      form: {
        pharmacist: ''
      },
      // 下拉框是否已关闭
      selectClosed: false
    };
  },
  computed: {
    // 弹窗显示状态的双向绑定
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      }
    },
    
    // 表单验证规则
    rules() {
      return {
        pharmacist: [
          { required: true, message: '请选择执业药师', trigger: 'blur' }
        ]
      };
    }
  },
  watch: {
    // 监听选中的执业药师变化，更新表单数据
    selectedPharmacist: {
      handler(newPharmacist) {
        this.form.pharmacist = newPharmacist || '';
      },
      immediate: true
    },
    
    // 监听弹窗显示状态，重置表单
    visible: {
      handler(newVisible) {
        if (newVisible) {
          this.form.pharmacist = this.selectedPharmacist || '';
          // 检查是否只有一个执业药师选项，如果是则自动选择
          this.$nextTick(() => {
            this.checkAutoSelectSinglePharmacist();
          });
        }
      }
    },
    
    // 监听执业药师列表变化，如果只有一个选项则自动选择
    pharmacistList: {
      handler(newList) {
        if (this.visible && newList && newList.length === 1) {
          this.$nextTick(() => {
            this.checkAutoSelectSinglePharmacist();
          });
        }
      },
      immediate: true
    }
  },
  methods: {
    // 检查是否只有一个执业药师选项，如果是则自动选择并确认
    checkAutoSelectSinglePharmacist() {
      // 只有在弹窗显示且执业药师列表只有一个选项时才自动选择
      if (this.visible && this.pharmacistList && this.pharmacistList.length === 1) {
        const singlePharmacist = this.pharmacistList[0];
        
        // 自动选择唯一的执业药师
        this.form.pharmacist = singlePharmacist.opmId;
        
        // 延迟一下再自动确认，确保界面更新完成
        setTimeout(() => {
          if (this.visible && this.form.pharmacist) {
            this.handleConfirm();
          }
        }, 300);
      }
    },

    // 统一的下拉框关闭方法
    closeSelectDropdown() {
      if (this.$refs.pharmacistSelect) {
        this.$refs.pharmacistSelect.blur();
        if (this.$refs.pharmacistSelect.visible !== undefined) {
          this.$refs.pharmacistSelect.visible = false;
        }
      }
    },

    // 统一的弹窗关闭方法
    closeDialog(shouldFocusProductInput = true) {
      // 先关闭下拉框
      this.closeSelectDropdown();
      
      // 关闭弹窗
      this.dialogVisible = false;
      
      // 重置表单数据
      this.form = {
        pharmacist: ''
      };

      // 移除事件监听器
      this.removeEventListeners();

      // 发出关闭事件
      this.$emit('close');

      // 聚焦到商品录入输入框
      if (shouldFocusProductInput) {
        this.$nextTick(() => {
          this.$emit('focus-product-input');
        });
      }
    },

    // 移除事件监听器
    removeEventListeners() {
      // 移除全局ESC键监听
      document.removeEventListener('keydown', this.handleGlobalEsc, true);
      
      // 移除下拉框的键盘事件监听
      if (this.$refs.pharmacistSelect && this.$refs.pharmacistSelect.$el) {
        const selectEl = this.$refs.pharmacistSelect.$el;
        selectEl.onkeydown = null;
      }
    },

    // 弹窗打开事件
    handleOpened() {
      this.selectClosed = false;
      this.$nextTick(() => {
        // 聚焦到下拉框
        if (this.$refs.pharmacistSelect) {
          this.$refs.pharmacistSelect.focus();
        }

        // 添加全局ESC键监听
        document.addEventListener('keydown', this.handleGlobalEsc, true);
      });
      this.$emit('opened');
    },

    // 弹窗关闭事件
    handleClose() {
      this.closeDialog(true);
    },

    // 确认选择
    handleConfirm() {
      this.$refs.pharmacistSelectFormRef.validate((valid) => {
        if (!valid) return;
        
        const pharmacist = this.form.pharmacist;
        
        // 关闭下拉框
        this.closeSelectDropdown();

        // 延迟关闭弹窗，确保下拉框已关闭
        setTimeout(() => {
          this.dialogVisible = false;
          this.removeEventListeners();
          this.$emit('confirm', { 
            pharmacist,
            pendingProduct: this.pendingProduct 
          });
        }, 100);
      });
    },

    // 全局ESC键处理
    handleGlobalEsc(event) {
      if (event.key === 'Escape' && this.dialogVisible) {
        event.preventDefault();
        event.stopPropagation();
        this.closeDialog(true);
      }
    },

    // 处理弹窗的键盘事件
    handleDialogKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.closeDialog(true);
      }
    },

    // 处理表单的键盘事件
    handleFormKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.closeDialog(true);
      }
    },

    // 执业药师选择键盘事件处理
    handleSelectKeyDown(event) {
      if (!this.dialogVisible) {
        return;
      }

      if (event.key === 'Enter') {
        // 检查下拉框是否已关闭且已选择药师
        if (this.selectClosed && this.form.pharmacist) {
          event.preventDefault();
          event.stopPropagation();
          this.handleConfirm();
        }
        return;
      } else if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.closeDialog(true);
        return;
      }
    },

    // 执业药师选择下拉框可见性变化处理
    onSelectVisibleChange(visible) {
      this.selectClosed = !visible;
    }
  },

  // 组件销毁时清理事件监听器
  beforeDestroy() {
    this.removeEventListeners();
  }
};
</script>

<style scoped>
::v-deep #pharmacistSelectForm .el-form-item__label {
  color: #606266 !important;
}
</style>
